import React from "react";
import { Link } from "react-router-dom";
import {
  Rocket, TrendingUp, Target, Megaphone, Globe, UserPlus, Settings2, Bot, Handshake,
  Compass, Wallet, Palette, Cpu, ShoppingCart, HeartHandshake, Users2, BarChart3,
  Check, ArrowRight,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { IMAGES } from "@/lib/images";
import BusinessEnquiryForm from "@/components/public/BusinessEnquiryForm";

const CLIENT_SERVICES = [
  { icon: Rocket, t: "Business Start-Up & Launch", items: ["Business planning", "Business structure & launch planning", "Operational setup", "Business systems", "Policies & processes", "Supplier research", "Business-document coordination", "Launch planning"] },
  { icon: TrendingUp, t: "Business Growth & Strategy", items: ["Growth planning", "Business diagnostics", "Profitability improvement", "Expansion planning", "KPI development", "Strategic planning", "Funding-readiness support"] },
  { icon: Target, t: "Sales, Leads & Contract Opportunities", items: ["Lead generation", "Customer acquisition support", "Contract / job opportunity research", "Outreach campaigns", "Tender opportunity research", "Sales pipeline development", "CRM setup", "Follow-up systems"] },
  { icon: Megaphone, t: "Marketing & Brand Growth", items: ["Business promotion", "Marketing strategy", "Social-media setup", "Marketing materials", "Campaign planning", "Brand positioning", "Content planning", "Customer acquisition campaigns"] },
  { icon: Globe, t: "Website & Digital Presence", items: ["Website creation", "Website improvement", "Website maintenance", "Landing pages", "Online booking / enquiry systems", "Digital business setup", "E-commerce support where appropriate"] },
  { icon: UserPlus, t: "Recruitment & Workforce Setup", items: ["Recruitment campaigns", "Candidate sourcing", "Job-ad preparation", "Applicant tracking", "Onboarding workflows", "Recruitment systems", "Staffing planning"] },
  { icon: Settings2, t: "Operations & Administration", items: ["Administrative setup", "CRM systems", "Quotations / templates", "SOP creation", "Workflow design", "Customer-service systems", "Supplier coordination", "Procurement support", "Business organisation"] },
  { icon: Bot, t: "AI & Automation", items: ["AI-assisted workflows", "Business automation", "CRM automation", "Customer-service automation", "Lead-management automation", "Reporting dashboards", "Productivity systems"] },
];

const FRACTIONAL = ["Fractional growth direction", "Operational oversight", "Implementation support", "Business reviews", "Monthly strategy", "KPI monitoring"];

const GROUP_CAPS = [
  { icon: Compass, t: "Strategy" }, { icon: Settings2, t: "Operations" }, { icon: Wallet, t: "Finance" },
  { icon: Megaphone, t: "Marketing" }, { icon: Palette, t: "Brand Development" }, { icon: Cpu, t: "Technology" },
  { icon: Bot, t: "AI & Automation" }, { icon: ShoppingCart, t: "Procurement" }, { icon: HeartHandshake, t: "Supplier Management" },
  { icon: UserPlus, t: "Recruitment" }, { icon: Users2, t: "Customer Experience" }, { icon: TrendingUp, t: "Business Development" },
  { icon: BarChart3, t: "Data & Reporting" },
];

export default function Services() {
  const { settings } = useSite();
  const formats = settings.service_formats || [];

  return (
    <div data-testid="page-services" className="bg-bd-ivory min-h-screen">
      {/* Hero */}
      <section className="pt-40 pb-24 bg-bd-black grain relative overflow-hidden">
        <img src={IMAGES.lobby} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-bd-black via-bd-black/80 to-bd-black/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="overline text-bd-gold mb-6">Business Services for Clients</p>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-bd-ivory leading-[0.95]">
              Build. Operate. Grow.
            </h1>
            <p className="mt-8 text-base md:text-lg text-bd-ivory/65 max-w-2xl leading-relaxed">
              Black Diamond Ventures Group helps entrepreneurs and growing businesses turn ideas into functioning, organised and scalable companies. We combine strategy with practical implementation, helping clients move beyond advice and actually get the work done.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/consultation" data-testid="services-cta-consultation" className="inline-flex items-center gap-2 bg-bd-gold text-bd-black hover:bg-bd-gold2 transition-colors duration-300 px-8 py-4 text-sm tracking-[0.15em] uppercase">
                Request a Consultation <ArrowRight size={16} />
              </Link>
              <a href="#enquire" data-testid="services-cta-discuss" className="inline-flex items-center gap-2 border border-bd-gold text-bd-gold hover:bg-bd-gold hover:text-bd-black transition-colors duration-300 px-8 py-4 text-sm tracking-[0.15em] uppercase">
                Discuss Your Business
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Business services for clients */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="overline text-bd-gold/80 mb-4">What we do for clients</p>
            <h2 className="font-serif text-3xl md:text-5xl text-bd-black max-w-3xl leading-tight">Practical, hands-on business services</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10 mt-12">
            {CLIENT_SERVICES.map((s, i) => (
              <Reveal key={s.t} delay={(i % 2) * 0.06}>
                <div data-testid={`service-cat-${i}`} className="bg-bd-ivory p-8 md:p-10 h-full hover:bg-white transition-colors duration-500">
                  <s.icon size={26} strokeWidth={1.2} className="text-bd-gold mb-5" />
                  <h3 className="font-serif text-2xl text-bd-black mb-5">{s.t}</h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                    {s.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-sm text-black/60">
                        <Check size={15} className="text-bd-gold mt-0.5 flex-shrink-0" strokeWidth={2} /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Fractional */}
          <Reveal>
            <div data-testid="service-fractional" className="mt-px bg-bd-black text-bd-ivory p-10 md:p-14 grain border border-black/10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-5">
                <Handshake size={30} strokeWidth={1.2} className="text-bd-gold mb-5" />
                <h3 className="font-serif text-3xl md:text-4xl mb-3">Fractional Business Support</h3>
                <p className="text-bd-ivory/60 text-sm leading-relaxed">Ongoing senior operational and growth support for businesses that need experienced direction without hiring a full internal team.</p>
              </div>
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {FRACTIONAL.map((f) => (
                  <div key={f} className="flex items-center gap-3 text-bd-ivory/80 border-b border-white/10 pb-3">
                    <Check size={16} className="text-bd-gold flex-shrink-0" strokeWidth={2} /> {f}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Service formats */}
      {formats.length > 0 && (
        <section className="py-20 md:py-28 bg-bd-charcoal grain">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <p className="overline text-bd-gold mb-4">How we work with you</p>
              <h2 className="font-serif text-3xl md:text-5xl text-bd-ivory max-w-3xl leading-tight">Ways to engage the group</h2>
              <p className="mt-6 text-sm md:text-base text-bd-ivory/55 max-w-2xl">Choose the format that fits your business — from a one-off diagnostic to ongoing, senior growth partnership.</p>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
              {formats.map((f, i) => (
                <Reveal key={f.name} delay={(i % 3) * 0.05}>
                  <div data-testid={`service-format-${i}`} className="bg-bd-black border border-white/10 p-8 h-full hover:border-bd-gold/40 transition-colors duration-500">
                    <h3 className="font-serif text-xl text-bd-ivory mb-3">{f.name}</h3>
                    <p className="text-sm text-bd-ivory/55 leading-relaxed">{f.description}</p>
                    {f.price_active && f.price && <p className="mt-4 text-bd-gold text-sm tracking-wide">{f.price}</p>}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Positioning */}
      <section className="py-24 md:py-32 bg-bd-black grain relative">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <Reveal>
            <p className="overline text-bd-gold mb-8">Strategy + Implementation + Systems + Growth</p>
            <p className="font-serif text-3xl md:text-5xl text-bd-ivory leading-tight">
              “We don't stop at advice. We help build the systems, processes and infrastructure needed to move the business forward.”
            </p>
          </Reveal>
        </div>
      </section>

      {/* Shared group capabilities */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="overline text-bd-gold/80 mb-4">Inside the group</p>
            <h2 className="font-serif text-3xl md:text-5xl text-bd-black max-w-3xl leading-tight">Shared group capabilities</h2>
            <p className="mt-6 text-sm md:text-base text-black/55 max-w-2xl">
              These capabilities are provided across the businesses Black Diamond Ventures Group builds and backs. They power our own ventures — distinct from the business services above, which external clients can engage us to deliver.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-black/10 border border-black/10 mt-12">
            {GROUP_CAPS.map((c, i) => (
              <Reveal key={c.t} delay={(i % 4) * 0.04}>
                <div className="bg-bd-ivory p-6 md:p-8 h-full flex items-center gap-4 hover:bg-white transition-colors duration-500">
                  <c.icon size={22} strokeWidth={1.2} className="text-bd-gold flex-shrink-0" />
                  <span className="font-serif text-lg text-bd-black">{c.t}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Inline enquiry */}
      <section id="enquire" className="py-20 md:py-28 bg-bd-black grain scroll-mt-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="overline text-bd-gold mb-4">Discuss your business</p>
            <h2 className="font-serif text-3xl md:text-5xl text-bd-ivory leading-tight">Tell us where you are and where you want to go</h2>
            <p className="mt-6 text-sm md:text-base text-bd-ivory/55 max-w-2xl">Share a little about your business and the support you need. We'll review it and arrange a consultation.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-12 bg-bd-charcoal border border-white/10 p-8 md:p-12">
              <BusinessEnquiryForm />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
